import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LexbeCase } from '../../../models/case.model';
import { CustomCaseFieldValue } from '../../../models/custom-case-field-value.model';
import { UserConfigs } from '../../../models/user-configs.model';
import { UserConfigsService } from '../../../services/user-configs.service';
import { CasesService } from '../../../services/cases.service';
import { Role } from '../../../models/role.model';
import { formatNumber } from '@angular/common';
import { FeatureConfigService } from 'src/app/services/feature-config.service';
import { BaseComponent } from '../../base.component';

@Component({
    selector: 'lex-case-details',
    templateUrl: './case-details.component.html',
    styleUrls: ['./case-details.component.scss'],
    standalone: false
})
export class CaseDetailsComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input()
    set currentCase(currentCase: LexbeCase) {
        this._currentCase = currentCase;
        this.updateCaseDetails(currentCase);
    }

    get currentCase() { return this._currentCase; }

    private _currentCase: LexbeCase;

    Role = Role;
    caseDetails: TitleValue[] = [];
    showMenu: boolean = true;
    userId: string;
    private userRoles: Role[];
    private subscription = new Subscription();

    constructor(private route: ActivatedRoute,
        private userService: UserConfigsService,
        private caseService: CasesService,
        public featureConfigService: FeatureConfigService) {
        super();
    }

    ngOnInit(): void {
        if (this.currentCase)
            return;

        this.showMenu = false;
        this.route.params.subscribe(params => {
            if (params.id)
                this.caseService.getCase(params.id).subscribe((currentCase: LexbeCase) => this.currentCase = currentCase);
            else
                this.subscription.add(this.userService.getUserConfigs().subscribe((config: UserConfigs) => this.currentCase = config.currentCase));
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    getEditLink(): string {
        if (this.currentCase)
            return `/case-details-edit/${this.currentCase.caseId}`;
        else
            return "";
    }

    isAccountAdmin(): boolean {
        return this.userService.inRoles(Role.AccountFeatureSet);
    }

    showEdit(): boolean {
        return this.userService.inRoles(Role.Operations, Role.Administrator, Role.AccountFeatureSet);
    }

    isNotLimited(): boolean {
        return !(this.userService.inRoles(Role.LimitedFeatureSet, Role.DocReviewFeatureSet));
    }

    updateCaseDetails(currentCase: LexbeCase) {
        if(!currentCase) return;

        this.subscription.add(this.userService.getUserConfigs().subscribe((userConfig: UserConfigs) => {
            this.userId = userConfig.userId;
            this.userRoles = userConfig.userRoles;
        }));
        LexbeCase.addComputedFields(currentCase);
        this.caseDetails = [];
        this.addTextField('Case', currentCase.caseName);
        this.addTextField('Case Id', currentCase.simpleCaseId.toString());
        if (currentCase.account)
            this.addTextField('Account', currentCase.account.accountName);

        this.addTextField('Case Number', currentCase.caseNumber);
        this.addTextField('Case Type', currentCase.caseType);
        this.addTextField('Venue', currentCase.caseVenue);
        this.addTextField('Lead Attorney', currentCase.leadAttorney);
        this.addTextField('Trier Of Fact', currentCase.trierOfFact);
        this.addTextField('Custom Designation', currentCase.customDesignation);
        this.addTextField('Comments', currentCase.caseComments);
		this.addTextField('Document Count', formatNumber(currentCase.caseDocCount, 'en-US', '1.0-0'));
		if(currentCase.useGigabytes)
			this.addTextField('Case Size (GB)', currentCase.displayTotalCaseSize.sizeInGigabytes);
		else
			this.addTextField('Case Size (GB)', currentCase.displayTotalCaseSize.sizeInGibibytes);

        if (!currentCase.customFields) return;

        currentCase.customFields.forEach((field: CustomCaseFieldValue) => {
            if (field.type === 1) // Textbox
                this.addTextField(field.title, field.text);
            if (field.type === 2) // Checkbox
                this.addCheckField(field.title, field.checked);
        });
    }

    private addTextField(title: string, value?: string) {
        if (value && value.length > 0)
            this.caseDetails.push(new TitleValue(title, value, null));
    }

    private addCheckField(title: string, checked?: boolean) {
        if (checked)
            this.caseDetails.push(new TitleValue(title, null, checked));
    }

    inRoles(...params: Role[]): boolean {
        return this.userService.inRoles(...params);
    }
}

class TitleValue {
    constructor(public title: string, public value?: string, public checked?: boolean) { }
}
